import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { buildBulkStoreUpdateData, mapCurrencyUpdateError } from './bulk-update-currencies';

type StoreDetails = NonNullable<inferRouterOutputs<AppRouter>['stores']['getById']>;

export type BulkSettingsStoreRef = {
  id: string;
  name: string;
};

export type BulkSettingsUpdateResult = {
  updated: number;
  skipped: number;
  failed: Array<{ storeId: string; storeName: string; reason: string }>;
};

export const isMultipleSubscriptionsAlreadySet = (
  store: StoreDetails,
  enabled: boolean,
) => (store.multipleSubscriptionsPerCustomerEnabled ?? true) === enabled;

export const buildBulkMultipleSubscriptionsUpdateData = (
  store: StoreDetails,
  multipleSubscriptionsPerCustomerEnabled: boolean,
) => ({
  ...buildBulkStoreUpdateData(store, store.presentmentCurrencies ?? []),
  multipleSubscriptionsPerCustomerEnabled,
});

export const createEmptyBulkSettingsResult = (): BulkSettingsUpdateResult => ({
  updated: 0,
  skipped: 0,
  failed: [],
});

type BulkSettingsUpdateDeps = {
  fetchStore: (storeId: string) => Promise<StoreDetails | null>;
  updateStore: (input: {
    storeId: string;
    updatedData: ReturnType<typeof buildBulkMultipleSubscriptionsUpdateData>;
  }) => Promise<unknown>;
};

const runStoreMultipleSubscriptionsUpdate = async (
  storeRef: BulkSettingsStoreRef,
  enabled: boolean,
  deps: BulkSettingsUpdateDeps,
) => {
  try {
    const store = await deps.fetchStore(storeRef.id);
    if (!store) throw new Error('Store not found');
    if (isMultipleSubscriptionsAlreadySet(store, enabled)) {
      return { storeRef, status: 'skipped' as const, reason: undefined };
    }
    await deps.updateStore({
      storeId: storeRef.id,
      updatedData: buildBulkMultipleSubscriptionsUpdateData(store, enabled),
    });
    return { storeRef, status: 'updated' as const, reason: undefined };
  } catch (error) {
    return {
      storeRef,
      status: 'failed' as const,
      reason: mapCurrencyUpdateError(error),
    };
  }
};

const aggregateSettingsOutcomes = (
  outcomes: Array<{
    storeRef: BulkSettingsStoreRef;
    status: 'updated' | 'skipped' | 'failed';
    reason?: string;
  }>,
): BulkSettingsUpdateResult => {
  const result = createEmptyBulkSettingsResult();
  outcomes.forEach((outcome) => {
    if (outcome.status === 'updated') result.updated += 1;
    if (outcome.status === 'skipped') result.skipped += 1;
    if (outcome.status === 'failed') {
      result.failed.push({
        storeId: outcome.storeRef.id,
        storeName: outcome.storeRef.name,
        reason: outcome.reason ?? 'unknown_error',
      });
    }
  });
  return result;
};

export const processBulkMultipleSubscriptionsUpdate = async (
  stores: BulkSettingsStoreRef[],
  multipleSubscriptionsPerCustomerEnabled: boolean,
  deps: BulkSettingsUpdateDeps,
): Promise<BulkSettingsUpdateResult> => {
  const outcomes = await Promise.all(
    stores.map((storeRef) =>
      runStoreMultipleSubscriptionsUpdate(storeRef, multipleSubscriptionsPerCustomerEnabled, deps),
    ),
  );
  return aggregateSettingsOutcomes(outcomes);
};
